import React, { useContext } from 'react';
import { Badge, OverlayTrigger, Popover, useClassNameMapper } from 'react-bootstrap';
import type { AppContextInterface } from '@aurora/shared-client/components/context/AppContext/AppContext';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useNodePolicies from '@aurora/shared-client/components/nodes/useNodePolicies';
import { canModerateNode } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import type { Article } from '@aurora/shared-generated/types/graphql-schema-types';
import { ModerationStatus } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import type { MessageViewFragment } from '../../../types/graphql-types';
import { useModerationStatus } from '../hooks/ModerationStatusContext';
import { useModerationStatusPolling } from '../useModerationStatusPolling';
import useTranslation from '../../useTranslation';
import {
  useModerationBadgeState,
  ModerationBadgeState,
  type ModerationBadgeResult
} from '../hooks/useModerationBadgeState';
import {
  useModerationApprovalTransition,
  APPROVED_STATES
} from '../hooks/useModerationApprovalTransition';
import localStyles from './ModerationStatusBadge.module.pcss';

const COMMUNITY_GUIDELINES_URL = '/communityguidelines';

/**
 * Suppresses approved badge states for persistent display.
 * Approval is the default expected state — persistent badges are only shown
 * for negative/action-required states. Transient approval notification
 * handles the live transition (useModerationApprovalTransition).
 *
 * KCOMM-815: Suppress persistent approved badges
 */
function suppressApprovedState(result: ModerationBadgeResult | null): ModerationBadgeResult | null {
  if (result && APPROVED_STATES.has(result.state)) {
    return null;
  }
  return result;
}

/**
 * Transient approval badge — shown inline when content transitions
 * from a non-approved state to an approved state during the user's session.
 * Auto-dismisses via the transition hook's timer.
 * Uses the same Badge pill as persistent badges, with the success variant.
 *
 * KCOMM-815: Transient Approval Notification
 */
function TransientApprovalBadge({
  cx,
  isDismissing
}: {
  cx: (...arguments_: unknown[]) => string;
  isDismissing: boolean;
}): React.ReactElement | null {
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MODERATION_STATUS_BADGE
  );

  if (textLoading) {
    return null;
  }

  return (
    <span
      className={cx('lia-moderation-status-badge-wrapper', 'lia-moderation-transient-badge', {
        'lia-is-dismissing': isDismissing
      })}
    >
      <Badge
        pill
        className={cx('lia-moderation-status-badge', 'lia-moderation-badge-success')}
        data-testid="ModerationStatusBadge.TRANSIENT_APPROVED"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cx('lia-moderation-badge-check-icon')}
        >
          <path
            d="M20 6L9 17l-5-5"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {formatMessage('transient.approved')}
      </Badge>
    </span>
  );
}

interface ModerationStatusBadgeProps {
  message: MessageViewFragment;
}

/**
 * Merges polled moderation data with initial message data for badge derivation.
 */
function buildBadgeInput(
  message: MessageViewFragment,
  polledStatus: ModerationStatus | undefined,
  polledAiResult: unknown | null
) {
  const { moderationData, latestAppeal } = message;
  const aiModerationResult =
    polledAiResult || ('aiModerationResult' in message ? message.aiModerationResult : null);
  // Derive aiProcessingStatus: if polled AI result has a verdict, processing is complete
  // regardless of what the initial message props say (they may be stale before refresh).
  const aiProcessingStatus = (aiModerationResult as { verdict?: string } | null)?.verdict
    ? 'COMPLETED'
    : 'aiProcessingStatus' in message
    ? message.aiProcessingStatus
    : null;

  // Determine effective status — polled takes precedence
  const effectiveStatus = polledStatus || moderationData?.status;

  // Determine effective visibility scope
  // If polled status is Approved or Rejected but original visibilityScope is PRIVATE_AND_UNMODERATED,
  // the badge should reflect the new status, not the stale visibility scope
  let effectiveVisibilityScope = message.visibilityScope;
  if (polledStatus === ModerationStatus.Approved || polledStatus === ModerationStatus.Rejected) {
    effectiveVisibilityScope = null;
  }

  return {
    visibilityScope: effectiveVisibilityScope,
    moderationData: moderationData
      ? {
          status: effectiveStatus,
          rejectReason: moderationData.rejectReason,
          rejectActorType: moderationData.rejectActorType,
          rejectTime: moderationData.rejectTime,
          approveActorType: (moderationData as Record<string, unknown>)?.approveActorType as
            | string
            | null
        }
      : null,
    latestAppeal: latestAppeal as {
      status?: string | null;
      category?: string | null;
      appealReason?: string | null;
      createdAt?: string | null;
      reviewedAt?: string | null;
      outcome?: string | null;
      reviewNotes?: string | null;
    } | null,
    aiProcessingStatus: aiProcessingStatus as string | null,
    aiModerationResult: aiModerationResult as {
      confidence?: number | null;
      processedAt?: string | null;
      reasonCodeDetails?: Array<{
        code: string;
        displayName?: string | null;
        description?: string | null;
        userMessage?: string | null;
      }> | null;
      flaggedComponent?: {
        type?: string | null;
        index?: number | null;
        verdict?: string | null;
        confidence?: number | null;
        reasoning?: string | null;
      } | null;
    } | null
  };
}

/**
 * Renders the info popover content based on the badge state payload.
 */
function renderPayloadContent(
  badgeResult: ModerationBadgeResult,
  formatMessage: (key: string, values?: Record<string, unknown>) => string
): React.ReactNode {
  const { state, payload } = badgeResult;

  switch (state) {
    case ModerationBadgeState.IN_REVIEW: {
      const hasAiProcessing =
        payload.aiProcessingStatus === 'PENDING' || payload.aiProcessingStatus === 'PROCESSING';
      const isLowConfidenceAccepted =
        payload.aiProcessingStatus === 'COMPLETED' &&
        (payload.verdict === 'ACCEPTED' || payload.verdict === 'Accepted') &&
        payload.actionTaken === false;

      return (
        <>
          <p>
            {formatMessage(
              hasAiProcessing
                ? 'payload.aiReviewing'
                : isLowConfidenceAccepted
                ? 'payload.lowConfidenceReview'
                : 'payload.awaitingModerator'
            )}
          </p>
          {isLowConfidenceAccepted && payload.reasonCodeDetails && (
            <div>
              <strong>{formatMessage('payload.violations')}</strong>
              <ul>
                {payload.reasonCodeDetails.map(detail => (
                  <li key={detail.code}>{detail.userMessage || detail.displayName}</li>
                ))}
              </ul>
            </div>
          )}
          {isLowConfidenceAccepted && (
            <p>
              <a href={COMMUNITY_GUIDELINES_URL} target="_blank" rel="noopener noreferrer">
                {formatMessage('payload.communityGuidelines')}
              </a>
            </p>
          )}
        </>
      );
    }

    case ModerationBadgeState.REJECTED: {
      const hasContent =
        payload.rejectReason ||
        payload.rejectActorType ||
        (payload.reasonCodeDetails && payload.reasonCodeDetails.length > 0) ||
        payload.rejectTime;
      return (
        <>
          {payload.rejectReason && (
            <p>{formatMessage('payload.rejectReason', { reason: payload.rejectReason })}</p>
          )}
          {payload.rejectActorType && (
            <p>{formatMessage('payload.rejectedBy', { actorType: payload.rejectActorType })}</p>
          )}
          {payload.reasonCodeDetails && payload.reasonCodeDetails.length > 0 && (
            <div>
              <strong>{formatMessage('payload.violations')}</strong>
              <ul>
                {payload.reasonCodeDetails.map(detail => (
                  <li key={detail.code}>
                    {detail.userMessage || detail.displayName || detail.code}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {payload.rejectTime && (
            <p className="text-muted">
              {formatMessage('payload.reviewedAt', {
                date: new Date(payload.rejectTime).toLocaleString()
              })}
            </p>
          )}
          {!hasContent && <p>{formatMessage('payload.rejectedFallback')}</p>}
        </>
      );
    }

    case ModerationBadgeState.AI_APPROVED: {
      return payload.processedAt ? (
        <p className="text-muted">
          {formatMessage('payload.reviewedAt', {
            date: new Date(payload.processedAt).toLocaleString()
          })}
        </p>
      ) : (
        <p>{formatMessage('payload.aiApprovedFallback')}</p>
      );
    }

    case ModerationBadgeState.MODERATOR_APPROVED: {
      return payload.approvalTime ? (
        <p className="text-muted">
          {formatMessage('payload.reviewedAt', {
            date: new Date(payload.approvalTime).toLocaleString()
          })}
        </p>
      ) : (
        <p>{formatMessage('payload.moderatorApprovedFallback')}</p>
      );
    }

    case ModerationBadgeState.APPEAL_APPROVED:
    case ModerationBadgeState.APPEAL_REJECTED: {
      const hasContent = payload.reviewNotes || payload.outcome || payload.reviewedAt;
      const fallbackKey =
        state === ModerationBadgeState.APPEAL_APPROVED
          ? 'payload.appealApprovedFallback'
          : 'payload.appealRejectedFallback';
      return (
        <>
          {payload.reviewNotes && (
            <p>{formatMessage('payload.reviewNotes', { notes: payload.reviewNotes })}</p>
          )}
          {payload.outcome && <p>{payload.outcome}</p>}
          {payload.reviewedAt && (
            <p className="text-muted">
              {formatMessage('payload.reviewedAt', {
                date: new Date(payload.reviewedAt).toLocaleString()
              })}
            </p>
          )}
          {!hasContent && <p>{formatMessage(fallbackKey)}</p>}
        </>
      );
    }

    case ModerationBadgeState.APPEAL_SUBMITTED: {
      const hasContent = payload.appealReason || payload.category || payload.createdAt;
      return (
        <>
          {payload.appealReason && <p>{payload.appealReason}</p>}
          {payload.category && <p>{payload.category}</p>}
          {payload.createdAt && (
            <p className="text-muted">
              {formatMessage('payload.reviewedAt', {
                date: new Date(payload.createdAt).toLocaleDateString()
              })}
            </p>
          )}
          {!hasContent && <p>{formatMessage('payload.appealSubmittedFallback')}</p>}
        </>
      );
    }

    default: {
      return null;
    }
  }
}

/**
 * Shared rendering logic for the badge + info popover.
 */
function BadgeRenderer({
  badgeResult,
  cx
}: {
  badgeResult: ModerationBadgeResult;
  cx: (...arguments_: unknown[]) => string;
}): React.ReactElement {
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MODERATION_STATUS_BADGE
  );

  if (textLoading) {
    return null;
  }

  const variantClassMap: Record<string, string> = {
    success: 'lia-moderation-badge-success',
    warning: 'lia-moderation-badge-warning',
    danger: 'lia-moderation-badge-danger',
    info: 'lia-moderation-badge-info'
  };

  const popover = (
    <Popover id={`moderation-badge-popover-${badgeResult.state}`}>
      <Popover.Content>
        <div className={cx('lia-moderation-badge-popover')}>
          {renderPayloadContent(badgeResult, formatMessage)}
        </div>
      </Popover.Content>
    </Popover>
  );

  return (
    <span
      className={cx('lia-moderation-status-badge-wrapper')}
      aria-label={formatMessage('ariaLabel', { status: formatMessage(badgeResult.labelKey) })}
    >
      <Badge
        pill
        className={cx('lia-moderation-status-badge', variantClassMap[badgeResult.variant])}
        data-testid={`ModerationStatusBadge.${badgeResult.state}`}
      >
        {formatMessage(badgeResult.labelKey)}
      </Badge>
      <OverlayTrigger trigger="click" placement="bottom" rootClose overlay={popover}>
        <button
          className={cx('lia-moderation-badge-info-btn')}
          aria-label={formatMessage('infoButton.ariaLabel')}
          type="button"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path
              d="M12 16v-4m0-4h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </OverlayTrigger>
    </span>
  );
}

/**
 * Topic-level badge variant — uses shared polling context.
 */
const TopicBadge: React.FC<{ message: MessageViewFragment }> = ({ message }) => {
  const cx = useClassNameMapper(localStyles);
  const { authUser, community } = useContext<AppContextInterface>(AppContext);
  const { data: nodePolicies } = useNodePolicies(
    module,
    { id: community.id, useCanModerateNode: true },
    !message
  );
  const {
    status: polledStatus,
    aiResult: polledAiResult,
    justApproved,
    isDismissingApproval
  } = useModerationStatus();
  const badgeInput = buildBadgeInput(message, polledStatus, polledAiResult);
  const fullBadgeResult = useModerationBadgeState(badgeInput);
  const displayBadgeResult = suppressApprovedState(fullBadgeResult);

  // Hide badge for blogs entirely
  if ((message as Article)?.contentWorkflow) {
    return null;
  }

  const isAuthor = authUser?.id === message?.author?.id;
  const isModerator = canModerateNode(nodePolicies?.coreNode);

  if (!isAuthor && !isModerator) {
    return null;
  }

  if (!justApproved && !displayBadgeResult) {
    return null;
  }

  return (
    <>
      {justApproved && <TransientApprovalBadge cx={cx} isDismissing={isDismissingApproval} />}
      {displayBadgeResult && <BadgeRenderer badgeResult={displayBadgeResult} cx={cx} />}
    </>
  );
};

/**
 * Reply-level badge variant — uses per-reply polling hook.
 */
const ReplyBadge: React.FC<{ message: MessageViewFragment }> = ({ message }) => {
  const cx = useClassNameMapper(localStyles);
  const { authUser, community } = useContext<AppContextInterface>(AppContext);
  const { data: nodePolicies } = useNodePolicies(
    module,
    { id: community.id, useCanModerateNode: true },
    !message
  );

  const { moderationData } = message;
  const isAuthor = authUser?.id === message?.author?.id;
  // Poll continuously for author's replies to pick up status resets after edits
  const enablePolling = isAuthor && Boolean(message?.id);
  const { status: polledStatus, aiResult: polledAiResult } = useModerationStatusPolling(
    message?.id,
    moderationData?.status as ModerationStatus | undefined,
    enablePolling
  );
  const badgeInput = buildBadgeInput(message, polledStatus, polledAiResult);
  const fullBadgeResult = useModerationBadgeState(badgeInput);
  const displayBadgeResult = suppressApprovedState(fullBadgeResult);
  const { justApproved, isDismissing } = useModerationApprovalTransition(fullBadgeResult);

  // Hide badge for draft messages
  if (message.visibilityScope === 'DRAFT') {
    return null;
  }

  const isModerator = canModerateNode(nodePolicies?.coreNode);

  if (!isAuthor && !isModerator) {
    return null;
  }

  if (!justApproved && !displayBadgeResult) {
    return null;
  }

  return (
    <>
      {justApproved && <TransientApprovalBadge cx={cx} isDismissing={isDismissing} />}
      {displayBadgeResult && <BadgeRenderer badgeResult={displayBadgeResult} cx={cx} />}
    </>
  );
};

/**
 * Persistent, non-dismissible moderation status badge.
 * Renders in the message card header for authors and moderators.
 *
 * Splits into TopicBadge/ReplyBadge internally because topic and reply
 * use different polling hooks (React hooks cannot be called conditionally).
 *
 * KCOMM-271: Persistent Moderation Status Badge
 */
const ModerationStatusBadge: React.FC<ModerationStatusBadgeProps> = ({ message }) => {
  if (message.depth === 0) {
    return <TopicBadge message={message} />;
  }
  return <ReplyBadge message={message} />;
};

export default ModerationStatusBadge;
